//src/pages/learner/pastAssestmentDetails.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getLearnerPastAssestment } from "../../api/learner/learnerApi"; // API to fetch past assessments
import useAuth from "../../hooks/useAuth";
import { IoMdArrowRoundBack } from "react-icons/io";

const PastAssessmentDetails = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [subjectiveResponses, setSubjectiveResponses] = useState([]);
  const [objectiveResponses, setObjectiveResponses] = useState([]);
  const [openAIResponses, setOpenAIResponses] = useState([]);
  const [subjectiverMarksPerQn, setSubjectiverMarksPerQn] = useState("");
  const [objectiveMarksPerQn, setObjectiveMarksPerQn] = useState("");
  const [openAIMarksPerQn, setOpenAIMarksPerQn] = useState("");
  const [questionCode, setQuestionCode] = useState("");
  const [subjectiveSectionMarks, setSubjectiveSectionMarks] = useState("");
  const [objectiveSectionMarks, setObjectiveSectionMarks] = useState("");
  const [openAISectionMarks, setOpenAISectionMarks] = useState("");
  const navigate = useNavigate();

  const backButtonHandler = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      try {
        const response = await getLearnerPastAssestment(user?.email); // Fetch all assessments for the user
        const { data } = response;

        const matchingAssessment = data?.data.find(
          (assessment) => assessment._id === id
        );

        setQuestionCode(matchingAssessment?.question_code);
        setSubjectiverMarksPerQn(
          matchingAssessment?.marks_per_question?.subjective
        );
        setObjectiveMarksPerQn(
          matchingAssessment?.marks_per_question?.objective
        );
        setOpenAIMarksPerQn(
          matchingAssessment?.marks_per_question?.conversational
        );

        setSubjectiveSectionMarks(
          matchingAssessment?.marks_per_section?.subjective
        );
        setObjectiveSectionMarks(
          matchingAssessment?.marks_per_section?.objective
        );
        setOpenAISectionMarks(
          matchingAssessment?.marks_per_section?.conversational
        );

        const responses = matchingAssessment?.responses || [];
        setSubjectiveResponses(
          responses.filter((res) => res.question_type === "subjective")
        );
        setObjectiveResponses(
          responses.filter((res) => res.question_type === "objective")
        );
        setOpenAIResponses(
          responses.filter((res) => res.question_type === "conversational")
        );
      } catch (error) {
        console.error("Error fetching past assessments:", error);
      }
    };

    fetchAssessmentDetails();
  }, [user?.email, id]);

  // Function to calculate total marks for a category
  const calculateTotalMarks = (responses) => {
    return responses.reduce((total, item) => total + (item.score || 0), 0);
  };

  // Function to round decimal marks to the desired number of decimal places
  const roundToDecimal = (num, decimals = 1) => {
    if (Number.isInteger(num)) return num; // No rounding needed for integers
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const calculateTotalSectionMarks = () => {
    return subjectiveSectionMarks + objectiveSectionMarks + openAISectionMarks;
  };

  // Calculate total marks for each category
  const totalSubjectiveMarks = calculateTotalMarks(subjectiveResponses);
  const totalObjectiveMarks = calculateTotalMarks(objectiveResponses);
  const totalOpenAIMarks = calculateTotalMarks(openAIResponses);

  // Calculate the total marks for all categories combined
  const totalMarks =
    totalSubjectiveMarks + totalObjectiveMarks + totalOpenAIMarks;

 return (
  <div className="relative w-full min-h-screen overflow-hidden bg-white text-gray-800 py-6">

    
    {/* <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-purple-300 opacity-30 blur-3xl rounded-full animate-pulse"></div>
    <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-300 opacity-30 blur-3xl rounded-full animate-pulse"></div> */}

    
    <div className="relative z-10">

     
      <div className="flex justify-between items-center lg:mx-60 md:mx-10 mx-4 mb-6">
        <button
          onClick={backButtonHandler}
          className="flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white shadow hover:scale-105 transition"
        >
          <IoMdArrowRoundBack className="text-xl" />
          <span className="font-semibold hidden md:block">All Assessments</span>
        </button>

        <h2 className="font-semibold text-sm md:text-lg bg-white shadow px-4 py-2 rounded-xl border">
          Assessment Code: {questionCode}
        </h2>
      </div>

      
      <div className="lg:mx-60 md:mx-10 mx-4 flex flex-col md:flex-row justify-between gap-4 mb-6">

        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 rounded-xl bg-yellow-100 shadow-sm">
            Total Marks: <strong>{calculateTotalSectionMarks()}</strong>
          </div>

          <div className="px-4 py-2 rounded-xl bg-green-100 shadow-sm">
            Marks Secured: <strong>{roundToDecimal(totalMarks)}</strong>
          </div>

          <div className="px-4 py-2 rounded-xl bg-blue-100 shadow-sm">
            Percentage:
            <strong>
              {" "}
              {roundToDecimal((totalMarks * 100) / calculateTotalSectionMarks())}%
            </strong>
          </div>
        </div>

        <select className="px-4 py-2 rounded-xl bg-white border shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
          <option value="allResponses">All Responses</option>
          <option value="subjective">Subjective Responses</option>
          <option value="objective">Objective Responses</option>
          <option value="conversational">Conversational Responses</option>
        </select>
      </div>

      
      <div className="lg:w-9/12 w-11/12 mx-auto flex flex-col">

       
        {subjectiveResponses?.length > 0 && (
          <div className="mb-10">
            <div className="flex justify-between items-center bg-purple-100 px-6 py-3 rounded-xl shadow-sm">
              <h1>
                Response Type : <strong>Subjective Responses</strong>
              </h1>
              <p>
                Total Marks:
                <strong>
                  {" "}
                  {roundToDecimal(totalSubjectiveMarks)} / {subjectiveSectionMarks}
                </strong>
              </p>
            </div>

            <div className="mt-4 rounded-xl overflow-hidden bg-white shadow border">
              <div className="flex bg-[#36369f] text-white px-4 py-3 text-sm">
                <p className="w-[10%]">Sl.</p>
                <p className="w-[70%]">Questions & Answers</p>
                <p className="w-[20%]">Marks</p>
              </div>

              {subjectiveResponses.map((item, index) => (
                <div key={index} className="border-t px-4 py-4 hover:bg-gray-50 transition">
                  <div className="flex">
                    <p className="w-[10%] font-bold">{index + 1}</p>
                    <h1 className="w-[70%]">{item.question_text}</h1>
                    <p className="w-[20%] font-semibold">
                      [{item.score}/{subjectiverMarksPerQn}]
                    </p>
                  </div>

                  <div className="flex mt-3">
                    <p className="w-[10%] font-bold">Ans:</p>
                    <p className="w-[70%]">{item.response}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

       
        {objectiveResponses?.length > 0 && (
          <div className="mb-10">
            <div className="flex justify-between items-center bg-purple-100 px-6 py-3 rounded-xl shadow-sm">
              <h1>
                Response Type : <strong>Objective Responses</strong>
              </h1>
              <p>
                Total Marks:
                <strong>
                  {" "}
                  {roundToDecimal(totalObjectiveMarks)} / {objectiveSectionMarks}
                </strong>
              </p>
            </div>

            <div className="mt-4 rounded-xl overflow-hidden bg-white shadow border">
              <div className="flex bg-[#36369f] to-indigo-500 text-white px-4 py-3 text-sm">
                <p className="w-[10%]">Sl.</p>
                <p className="w-[70%]">Questions & Answers</p>
                <p className="w-[20%]">Marks</p>
              </div>

              {objectiveResponses.map((item, index) => (
                <div key={index} className="border-t px-4 py-4 hover:bg-gray-50 transition">
                  <div className="flex">
                    <p className="w-[10%] font-bold">{index + 1}</p>
                    <h1 className="w-[70%]">{item.question_text}</h1>
                    <p className="w-[20%] font-semibold">
                      [{item.score}/{objectiveMarksPerQn}]
                    </p>
                  </div>

                  <div className="flex mt-3">
                    <p className="w-[10%] font-bold">Ans:</p>
                    <p className="w-[70%]">{item.response}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        
        {openAIResponses?.length > 0 && (
          <div className="mb-10">
            <div className="flex justify-between items-center bg-purple-100 px-6 py-3 rounded-xl shadow-sm">
              <h1>
                Response Type : <strong>Conversational Responses</strong>
              </h1>
              <p>
                Total Marks:
                <strong>
                  {" "}
                  {roundToDecimal(totalOpenAIMarks)} / {openAISectionMarks}
                </strong>
              </p>
            </div>

            <div className="mt-4 rounded-xl overflow-hidden bg-white shadow border">
              <div className="flex bg-[#36369f] text-white px-4 py-3 text-sm">
                <p className="w-[10%]">Sl.</p>
                <p className="w-[70%]">Questions & Answers</p>
                <p className="w-[20%]">Marks</p>
              </div>

              {openAIResponses.map((item, index) => (
                <div key={index} className="border-t px-4 py-4 hover:bg-gray-50 transition">
                  <div className="flex">
                    <p className="w-[10%] font-bold">{index + 1}</p>
                    <h1 className="w-[70%]">{item.question_text}</h1>
                    <p className="w-[20%] font-semibold">
                      [{item.score}/{openAIMarksPerQn}]
                    </p>
                  </div>

                  <div className="flex mt-3">
                    <p className="w-[10%] font-bold">Ans:</p>
                    <p className="w-[70%]">{item.response}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  </div>
);
};

export default PastAssessmentDetails;
